import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useOutletContext } from 'react-router'
import React, { Dispatch, MutableRefObject, SetStateAction, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAuthContext } from 'contexts/auth.context'
import { Funcionalidades } from 'utils/helpers/funcionalidades'
import {
  SocioeducationAccompanimentDataDefaultValues,
  SocioeducationAccompanimentDataSchema,
  SocioeducationAccompanimentDataType,
} from '../../../types/socioeducation-accompaniment-data.type'
import { useMeioAbertoAcompanhamento } from 'hooks/meio-aberto-acompanhamento.hook'
import CustomAccordion from 'components/Accordion'
import SchoolingLearningWorkProcess from 'pages/SocioeducationMeasureEdit/components/SchoolingLearningWorkProcess'
import ComprehensiveHealth from 'pages/SocioeducationMeasureEdit/components/ComprehensiveHealth'
import CultureSports from 'pages/SocioeducationMeasureEdit/components/CultureSports'
import SocialAssistanceNetwork from 'pages/SocioeducationMeasureEdit/components/SocialAssistanceNetwork'

type OutletContextType = {
  currentStep: number
  pessoaFisicaId?: number
  formRefs: MutableRefObject<Record<number, { submit: () => Promise<void> | void }>>
  setPessoaFisicaId: Dispatch<SetStateAction<number | null | undefined>>
}

export const MeioAbertoAcompanhamentoPage = () => {
  const { id } = useParams()
  const { handleHasPermission } = useAuthContext()

  const { currentStep, formRefs } = useOutletContext<OutletContextType>()
  const methods = useForm<SocioeducationAccompanimentDataType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(SocioeducationAccompanimentDataSchema),
    defaultValues: SocioeducationAccompanimentDataDefaultValues,
  })

  const { dadosAcompanhamento, handleSalvar: handleSalvarMeioAberto } =
    useMeioAbertoAcompanhamento(id)

  const {
    handleSubmit,
    clearErrors,
    control,
    reset,
    watch,
    setError,
    formState: { isDirty, dirtyFields },
  } = methods

  const doNotHasEditPermission = !handleHasPermission(Funcionalidades.meioAberto.editar)

  const hasDirtyFields = Object.keys(dirtyFields).length > 0 && dirtyFields.constructor === Object

  useEffect(() => {
    if (dadosAcompanhamento) {
      reset(dadosAcompanhamento)
    }
  }, [dadosAcompanhamento])

  const handleSalvar = async (data?: SocioeducationAccompanimentDataType) => {
    await handleSalvarMeioAberto(data)
    return Promise.resolve()
  }

  const onError = (errors: FieldErrors<SocioeducationAccompanimentDataType>) => {
    const recursiveSetError = (errors: any, parentPath = '') => {
      clearErrors()
      Object.entries(errors).forEach(([key, error]: any) => {
        const path = parentPath ? `${parentPath}.${key}` : key

        if (error?.ref) {
          setError(error.ref.name, { type: error.type, message: error.message })
        } else if (typeof error === 'object') {
          recursiveSetError(error, path)
        }
      })
    }

    recursiveSetError(errors)
    return Promise.reject(errors)
  }

  formRefs.current[currentStep] = {
    submit: async () => {
      if (isDirty && hasDirtyFields && handleHasPermission(Funcionalidades.meioAberto.editar)) {
        await handleSubmit(handleSalvar, onError)()
      }
      return Promise.resolve()
    },
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleSalvar, onError)}>
        <CustomAccordion
          title='INFORMAÇÕES SOBRE ESCOLARIZAÇÃO E PROCESSO DE APRENDIZAGEM, PROFISSIONALIZAÇÃO E
          TRABALHO'
        >
          <SchoolingLearningWorkProcess
            control={control}
            doNotHasEditPermission={doNotHasEditPermission}
            watch={watch}
          />
        </CustomAccordion>
        <CustomAccordion title='INFORMAÇÕES SOBRE SAÚDE INTEGRAL'>
          <ComprehensiveHealth
            control={control}
            doNotHasEditPermission={doNotHasEditPermission}
            watch={watch}
          />
        </CustomAccordion>
        <CustomAccordion title='INFORMAÇÕES SOBRE CULTURA E ESPORTE'>
          <CultureSports control={control} doNotHasEditPermission={doNotHasEditPermission} />
        </CustomAccordion>
        <CustomAccordion title='INFORMAÇÕES SOBRE REDE SOCIOASSISTENCIAL'>
          <SocialAssistanceNetwork
            control={control}
            doNotHasEditPermission={doNotHasEditPermission}
            watch={watch}
          />
        </CustomAccordion>
      </form>
    </FormProvider>
  )
}
